import React, {useState} from "react";
import {Contractor} from "../Interfaces/IDOOP";
import {useMain} from "../../../Utils/SessionControls/mainContext";
import {setProjectMeta} from "../BackendFacade/apiCalls";
import {HubPathRouting} from "../../../HubFramework/pathBuilder";
import OdxSelect, {OdxSelectOptionProps} from "../../../OdxComponents/NativeOdx/FormControls/Odx-Select";
import OdxInputField from "../../../OdxComponents/NativeOdx/FormControls/Odx-InputField";
import OdxModal from "../../../OdxComponents/NativeOdx/Modal/Odx-Modal";
import {odxIconSets} from "../../../OdxComponents/constants";
import {isUserroleSufficient} from "../../../Utils/authorization";
import {UserRoles} from "../../../Utils";
import {useDoopData} from "../doopDataContext";

interface EditProjectModalProps {
    name: string;
    contractors: Contractor[];
    value: string | string[] | boolean;
    closePopup: () => void;
}

const EditProjectModal: React.FC<EditProjectModalProps> = ({name, contractors, value, closePopup}) => {
    const [inputValue, setInputValue] = useState<string | string[] | boolean>(value);
    const [contractorList, setContractorList] = useState<Contractor[]>(contractors);
    const [selectedAreas, setSelectedAreas] = useState<string[]>(Array.isArray(value) ? value : []);
    const {meta, areas, refresh} = useDoopData();
    const {setLoading, createErrorToast} = useMain();

    const addContractor = () => {
        if (typeof inputValue !== "string" || inputValue.trim() === "") {
            createErrorToast("Error", "Contractor name cannot be empty");
            return;
        }
        if (!contractorList.find(c => c.Name === inputValue)) {
            setContractorList([...contractorList, {Name: inputValue} as Contractor]);
            setInputValue("");
        }
    };

    const deleteContractor = (item: Contractor) => {
        setContractorList(contractorList.filter(i => i !== item));
    };

    const submitProject = () => {
        if (!meta) {
            createErrorToast("Error", "Project meta not available");
            return;
        }
        const updatedMeta = {...meta};
        switch (name) {
            case "doopAreas":
                updatedMeta.doopAreas = selectedAreas.map(i => i);
                break;
            case "contractors":
                updatedMeta.contractors = contractorList;
                break;
            default:
                updatedMeta[name] = inputValue;
        }
        setLoading(true);
        setProjectMeta(
            HubPathRouting.currentProject.id,
            HubPathRouting.currentModule.id,
            {projectMetaToUpdate: updatedMeta}
        ).then(response => {
            setLoading(false);
            if (!response.success) {
                createErrorToast("Error", response.message);
            } else {
                refresh().then(closePopup).catch(() => {
                    createErrorToast("Error", "Failed to refresh project data");
                });
            }
        });
    };

    const renderContent = () => {
        if (name === "doopAreas") {
            return (
                <OdxSelect
                    options={getCurrentDoopAreas()}
                    value={selectedAreas}
                    id={"DoopConfig_Areas_Value"}
                    onChange={(selected) => setSelectedAreas(Array.isArray(selected) ? selected : [])}
                    placeholder="Area"
                    isMulti={true}
                />
            );
        }
        if (name === "contractors") {
            return (
                <div>
                    <div className="flex space-x-2">
                        <OdxInputField
                            placeholder="Contractor Name"
                            isRequired={true}
                            id="contractorName"
                            label="Contractor Name"
                            value={typeof inputValue === "string" ? inputValue : ""}
                            onChange={(e) => setInputValue(e.target.value)}
                        />
                        <div style={{marginTop: '14px'}}>
                            <odx-button
                                id="Doop_Config_Contractors_AddBtn"
                                variant="accent"
                                size="lg"
                                type="button"
                                className="self-end"
                                onClick={addContractor}
                            >
                                Add
                            </odx-button>
                        </div>
                    </div>
                    <odx-separator style={{margin: '1rem 0'}} />
                    <div className="mb-4">
                        {contractorList.map((item, index) => (
                            <odx-button
                                key={index}
                                variant="neutral"
                                size="lg"
                                type="button"
                                id={`Doop_Config_Contractor_Remove_${index}`}
                                onClick={() => deleteContractor(item)}
                                className="mr-2"
                            >
                                {item.Name}
                                <odx-icon slot="suffix" name={`${odxIconSets.core}::trash`} size="sm"></odx-icon>
                            </odx-button>
                        ))}
                    </div>

                </div>
            );
        }
        return (
            <OdxInputField
                placeholder={`Enter ${name}`}
                isRequired={true}
                id={name}
                label={name}
                value={typeof inputValue === "string" ? inputValue : ""}
                onChange={(e) => setInputValue(e.target.value)}
            />
        );
    };

    const getCurrentDoopAreas = (): OdxSelectOptionProps[] => {
        return areas?.map(area => {
            const name = area.name.split('\\').pop() || '';
            return {label: name, value: area.name};
        }) || [];
    };

    const canEdit = isUserroleSufficient(UserRoles.editor, HubPathRouting.currentProject?.id);

    return (
        <OdxModal
            size="small"
            title={`Edit ${name}`}
            onClose={closePopup}
            isOpen={true}
            variant="sidesheet"
            footerButtons={[
                <odx-button
                    key="cancel"
                    variant="neutral"
                    size="lg"
                    type="button"
                    onClick={closePopup}
                >
                    Cancel
                </odx-button>,
                canEdit ? (
                    <odx-button
                        key="save"
                        id="EditPopUpApplyBtn"
                        variant="accent"
                        size="lg"
                        type="button"
                        onClick={submitProject}
                    >
                        Save
                    </odx-button>
                ) : null
            ].filter(Boolean)}
        >
            <div className="space-y-4">{renderContent()}</div>
        </OdxModal>
    );
};

export default EditProjectModal;
